import { Link } from 'react-router-dom';
import type { StatusData } from '../page';

// ── Status badge component ─────────────────────────────────────────────────────
type StatusLevel = 'active' | 'prepared' | 'pending' | 'warning' | 'error';

interface StatusRowProps {
  icon: string;
  name: string;
  description: string;
  level: StatusLevel;
  note?: string;
}

const LEVEL_CFG: Record<StatusLevel, { dot: string; badge: string; label: string }> = {
  active:   { dot: 'bg-emerald-400 animate-pulse', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',   label: 'Active' },
  prepared: { dot: 'bg-amber-400',                  badge: 'bg-amber-50 text-amber-700 border-amber-200',         label: 'Prepared' },
  pending:  { dot: 'bg-slate-300',                  badge: 'bg-slate-100 text-slate-500 border-slate-200',        label: 'Pending' },
  warning:  { dot: 'bg-orange-400',                 badge: 'bg-orange-50 text-orange-700 border-orange-200',      label: 'Warning' },
  error:    { dot: 'bg-red-400',                    badge: 'bg-red-50 text-red-600 border-red-200',               label: 'Error' },
};

function StatusRow({ icon, name, description, level, note }: StatusRowProps) {
  const cfg = LEVEL_CFG[level];
  return (
    <div className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0">
      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 shrink-0">
        <i className={`${icon} text-slate-500 text-sm`}></i>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-slate-800 text-sm font-medium">{name}</p>
        <p className="text-slate-400 text-xs">{description}</p>
        {note && <p className="text-slate-400 text-[10px] mt-0.5 italic">{note}</p>}
      </div>
      <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold whitespace-nowrap shrink-0 ${cfg.badge}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></span>
        {cfg.label}
      </span>
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, color, linkTo }: {
  icon: string; label: string; value: number | string;
  color: string; linkTo?: string;
}) {
  const inner = (
    <div className={`bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3 ${linkTo ? 'hover:border-blue-300 transition-colors' : ''}`}>
      <span className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${color}`}>
        <i className={`${icon} text-base`}></i>
      </span>
      <div>
        <p className="text-slate-400 text-xs uppercase tracking-wider">{label}</p>
        <p className="text-slate-900 text-2xl font-bold leading-tight">{value}</p>
      </div>
    </div>
  );
  if (linkTo) return <Link to={linkTo}>{inner}</Link>;
  return inner;
}

// ── Component ─────────────────────────────────────────────────────────────────
interface Props { data: StatusData; }

export default function CoreTab({ data }: Props) {
  return (
    <div className="space-y-6">

      {/* ── Summary stats ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon="ri-building-2-line"     label="Total Tenants"  value={data.tenantsTotal}    color="text-blue-600 bg-blue-50"     linkTo="/sa/tenants" />
        <StatCard icon="ri-checkbox-circle-line" label="Active"         value={data.tenantsActive}   color="text-emerald-600 bg-emerald-50" />
        <StatCard icon="ri-time-line"            label="Trial"          value={data.tenantsTrial}    color="text-amber-600 bg-amber-50" />
        <StatCard icon="ri-error-warning-line"   label="Suspended"      value={data.tenantsSuspended} color="text-red-500 bg-red-50" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon="ri-price-tag-3-line"  label="Plans"       value={data.plansCount}    color="text-violet-600 bg-violet-50"  linkTo="/sa/plans" />
        <StatCard icon="ri-puzzle-line"       label="Modules"     value={data.allModules.length} color="text-cyan-600 bg-cyan-50"  linkTo="/sa/modules" />
        <StatCard icon="ri-team-line"         label="Users"       value={data.usersCount}    color="text-slate-600 bg-slate-100"  linkTo="/sa/users" />
        <StatCard icon="ri-file-list-3-line"  label="Audit Events" value={data.auditLogsCount} color="text-indigo-600 bg-indigo-50" linkTo="/sa/audit-logs" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Infrastructure status ── */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2">
            <i className="ri-server-line text-slate-400 text-sm"></i>
            <h3 className="text-slate-700 text-sm font-semibold">Infrastructure</h3>
          </div>
          <div className="px-5 py-1">
            <StatusRow icon="ri-database-2-line"  name="Supabase Database"  description="PostgreSQL — primary data store"       level={data.dbAlive ? 'active' : 'error'} note={data.dbAlive ? undefined : 'Connection failed — check Supabase project'} />
            <StatusRow icon="ri-shield-user-line" name="Supabase Auth"      description="Authentication & session management"   level={data.authAlive ? 'active' : 'error'} />
            <StatusRow icon="ri-cloud-line"       name="Admin Panel"        description="React SPA — tenant admin interface"    level="active" />
            <StatusRow icon="ri-shield-star-line" name="SA Panel"           description="Super Admin control center"           level="active" />
            <StatusRow icon="ri-hard-drive-line"  name="VPS Storage"        description="Object storage for photos/media"       level="prepared" note="Backend endpoint not yet deployed" />
            <StatusRow icon="ri-code-s-slash-line" name="Edge Functions"    description="Supabase serverless functions"         level="prepared" note="API routes not yet wired" />
          </div>
        </div>

        {/* ── Tenant health ── */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2">
            <i className="ri-building-2-line text-slate-400 text-sm"></i>
            <h3 className="text-slate-700 text-sm font-semibold">Tenant Health</h3>
          </div>
          <div className="px-5 py-4 space-y-3">
            {/* Status breakdown bar */}
            {data.tenantsTotal > 0 && (
              <div className="space-y-2">
                <p className="text-slate-400 text-xs uppercase tracking-wider">Tenant Distribution</p>
                <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 gap-px">
                  {data.tenantsActive > 0 && (
                    <div className="bg-emerald-400 rounded-l-full" style={{ width: `${(data.tenantsActive / data.tenantsTotal) * 100}%` }} title={`Active: ${data.tenantsActive}`} />
                  )}
                  {data.tenantsTrial > 0 && (
                    <div className="bg-amber-400" style={{ width: `${(data.tenantsTrial / data.tenantsTotal) * 100}%` }} title={`Trial: ${data.tenantsTrial}`} />
                  )}
                  {data.tenantsSuspended > 0 && (
                    <div className="bg-red-400 rounded-r-full" style={{ width: `${(data.tenantsSuspended / data.tenantsTotal) * 100}%` }} title={`Suspended: ${data.tenantsSuspended}`} />
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs">
                  {[
                    { color: 'bg-emerald-400', label: 'Active',    count: data.tenantsActive },
                    { color: 'bg-amber-400',   label: 'Trial',     count: data.tenantsTrial },
                    { color: 'bg-red-400',     label: 'Suspended', count: data.tenantsSuspended },
                  ].map((s) => (
                    <div key={s.label} className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${s.color}`}></span>
                      <span className="text-slate-500">{s.label}: <strong className="text-slate-700">{s.count}</strong></span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Module & billing summary */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              {[
                { icon: 'ri-puzzle-line',         label: 'Active modules in platform',   value: `${data.activeModulesCount} / ${data.allModules.length}` },
                { icon: 'ri-toggle-line',          label: 'Module activations (tenants)', value: data.tenantModulesEnabled },
                { icon: 'ri-time-line',            label: 'Pending module requests',       value: data.moduleRequestsPending },
                { icon: 'ri-global-line',          label: 'Pending domain requests',       value: data.domainRequestsPending },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2.5 p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="w-6 h-6 flex items-center justify-center rounded-lg bg-white border border-slate-200 shrink-0">
                    <i className={`${item.icon} text-slate-400 text-xs`}></i>
                  </span>
                  <div className="min-w-0">
                    <p className="text-slate-800 text-sm font-bold leading-tight">{item.value}</p>
                    <p className="text-slate-400 text-[10px] leading-tight line-clamp-2">{item.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Recent actions shortcut ── */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 flex items-center justify-center rounded-xl bg-blue-600 text-white shrink-0">
            <i className="ri-shield-check-line text-base"></i>
          </span>
          <div>
            <p className="text-blue-900 text-sm font-semibold">Platform is running normally</p>
            <p className="text-blue-600 text-xs">Database connected · Auth active · {data.tenantsActive} active tenants</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data.moduleRequestsPending > 0 && (
            <Link to="/sa/module-requests" className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-400 hover:bg-amber-500 text-black text-xs font-semibold rounded-lg transition-colors whitespace-nowrap">
              <i className="ri-puzzle-line"></i>
              {data.moduleRequestsPending} addon request{data.moduleRequestsPending > 1 ? 's' : ''}
            </Link>
          )}
          {data.domainRequestsPending > 0 && (
            <Link to="/sa/domain-requests" className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-400 hover:bg-amber-500 text-black text-xs font-semibold rounded-lg transition-colors whitespace-nowrap">
              <i className="ri-global-line"></i>
              {data.domainRequestsPending} domain request{data.domainRequestsPending > 1 ? 's' : ''}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
